/*
 * RentTick Hostel Management System
 * Copyright (c) 2025 [Your Name/Company Name]. All rights reserved.
 * 
 * This software and associated documentation files (the "Software") are protected
 * by copyright law and international treaties. Unauthorized reproduction or
 * distribution of this software, or any portion of it, may result in severe
 * civil and criminal penalties, and will be prosecuted to the maximum extent
 * possible under the law.
 */

import React from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate, Navigate } from 'react-router-dom';
import { setNavigate } from './utils/navigationHelper';
import { SessionProvider } from './hooks/useSessionInfo';
import { MenuCatalogProvider } from './hooks/useMenuCatalog';
import tenantStorage from './utils/tenantStorage';
import LoginForm from './components/login/LoginForm';
import ForgotPasswordForm from './components/login/ForgotPasswordForm';
import ChangePasswordForm from './components/login/ChangePasswordForm';
import ChangeUsernameForm from './components/profile/ChangeUsernameForm';
import Home from './components/home/Home';
import PGManagement from './components/payingguest/PGManagement';

import AddPGDetails from './components/payingguest/AddPGDetails';
import PGDetailView from './components/payingguest/PGDetailView';
import ViewPGList from './components/payingguest/ViewPGList';
import BuildingManagement from './components/building/BuildingManagement';
import AddBuilding from './components/building/AddBuilding';
import ViewBuildings from './components/building/ViewBuildings';
import ViewBuildingDetails from './components/building/ViewBuildingDetails';
import VacateBuilding from './components/building/VacateBuilding';
import CreateRentInvoices from './components/building/CreateRentInvoices';
import ViewRentalInvoiceStatus from './components/building/ViewRentalInvoiceStatus';
import 'bootstrap/dist/css/bootstrap.min.css';
import SessionTimeoutHandler from './components/session/SessionTimeoutHandler';
import BedAllocationManagement from './components/bedallocation/BedAllocationManagement'
import AllocateBed from './components/bedallocation/AllocateBed';
import MoveBed from './components/bedallocation/MoveBed'
import ViewAllocatedBeds from './components/bedallocation/ViewAllocatedBeds';
import ViewVacantBeds from './components/bedallocation/ViewVacantBeds';
import ViewAllocationHistory from './components/bedallocation/ViewAllocationHistory';
import ViewSoonVacatingBeds from './components/bedallocation/ViewSoonVacatingBeds';
import ViewAllocationDetails from './components/bedallocation/ViewAllocationDetails';
import BedPricing from './components/bedallocation/BedPricing';
import TermsAndConditions from './components/terms/TermsAndConditions';
import PrivacyPolicy from './components/terms/PrivacyPolicy';
import ContactUs from './components/contact/ContactUs';
import AboutUs from './components/about/AboutUs';
import StaffManagement from './components/staff/StaffManagement';
import AddStaff from './components/staff/AddStaff';
import ViewStaffs from './components/staff/ViewStaffs';
import ViewStaffDetails from './components/staff/ViewStaffDetails';
import StaffTypesList from './components/staff/StaffTypesList';
import StaffComplaintMapping from './components/staff/StaffComplaintMapping';
import ComplaintManagement from './components/complaints/ComplaintManagement';
import AddComplaint from './components/complaints/AddComplaint';
import ViewOpenComplaints from './components/complaints/ViewOpenComplaints';
import ViewComplaintsHistory from './components/complaints/ViewComplaintsHistory';
import ViewComplaintDetails from './components/complaints/ViewComplaintDetails';
import MyAssignedComplaints from './components/complaints/MyAssignedComplaints';
import PaymentManagement from './components/payments/PaymentManagement';
import GenerateMonthlyRentalInvoice from './components/payments/GenerateMonthlyRentalInvoice';
import ViewDraftInvoices from './components/payments/ViewDraftInvoices';
import ViewUnpaidInvoices from './components/payments/ViewUnpaidInvoices';
import ViewInvoiceDetails from './components/payments/ViewInvoiceDetails';
import ViewPaymentDetail from './components/payments/ViewPaymentDetail';
import CreateInvoice from './components/payments/CreateInvoice';
import PaymentTypesList from './components/payments/PaymentTypesList';
import PaymentMethodsList from './components/payments/PaymentMethodsList';
import AddPayment from './components/payments/AddPayment';
import AddExpense from './components/payments/AddExpense';
import ViewExpenses from './components/payments/ViewExpenses';
import ViewPayments from './components/payments/ViewPayments';
import InvoiceItemTypesList from './components/invoicing/InvoiceItemTypesList';
import SettingsManagement from './components/settings/SettingsManagement';
import ExpenseCategoriesList from './components/settings/ExpenseCategoriesList';
import RoleSettings from './components/settings/RoleSettings';
import AccessControl from './components/accesscontrol/AccessControl';
import ManageUsers from './components/accesscontrol/ManageUsers';
import PaymentSettings from './components/settings/PaymentSettings';
import WhatsAppSettings from './components/settings/WhatsAppSettings';
import WhatsAppTemplateManagement from './components/settings/WhatsAppTemplateManagement';
import WhatsAppTemplateTags from './components/settings/WhatsAppTemplateTags';
import SendWhatsAppTemplateMessage from './components/settings/SendWhatsAppTemplateMessage';
import ReportManagement from './components/reports/ReportManagement';
import WhatsAppLogReport from './components/reports/WhatsAppLogReport';
import AllocationReport from './components/reports/AllocationReport';
import PaymentAnalyticsReport from './components/reports/PaymentAnalyticsReport';
import ComplaintsReport from './components/reports/ComplaintsReport';
import RentCollectionReport from './components/reports/RentCollectionReport';
import ExpenseReport from './components/reports/ExpenseReport';
import JobReportList from './components/reports/JobReportList';
import ViewJobDetails from './components/reports/ViewJobDetails';
import AddEBBillToInvoices from './components/payments/AddEBBillToInvoices';
import PGAllocation from './components/pg/PGAllocation';
import PGComplaints from './components/pg/PGComplaints';
import PGInvoices from './components/pg/PGInvoices';
import PGPayments from './components/pg/PGPayments';

// Navigation setup component
const NavigationSetup = () => {
    const navigate = useNavigate();
    React.useEffect(() => {
        setNavigate(navigate);
    }, [navigate]);
    return null;
};

// Root redirect component - checks if user is logged in
const RootRedirect = () => {
    const isLoggedIn = tenantStorage.getItem('accessToken') !== null;
    return isLoggedIn ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
};

function App() {
    return (
        <SessionProvider>
        <Router>
          <MenuCatalogProvider>
            <NavigationSetup />
            <SessionTimeoutHandler />
            <div className="App">
                <Routes>
                    {/* Root path - redirect based on login status */}
                    <Route path="/" element={<RootRedirect />} />
                    
                    {/* Login route */}
                    <Route path="/login" element={<LoginForm />} />
                    <Route path="/login/forgot-password" element={<ForgotPasswordForm />} />
                    
                    {/* Public pages - accessible without login */}
                    <Route path="/terms" element={<TermsAndConditions />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/about" element={<AboutUs />} />
                    
                    {/* Protected routes - require login */}
                    <Route path="/dashboard" element={<Home />} />

                    {/* Paying Guest self-service portal */}
                    <Route path="/pg/allocation" element={<PGAllocation />} />
                    <Route path="/pg/complaints" element={<PGComplaints />} />
                    <Route path="/pg/complaints/add" element={<AddComplaint />} />
                    <Route path="/pg/complaints/view/:complaintId" element={<ViewComplaintDetails />} />
                    <Route path="/pg/invoices" element={<PGInvoices />} />
                    <Route path="/pg/payments" element={<PGPayments />} />

                    {/* Nested routes for PG Management */}
                    <Route path="/paying-guest" element={<PGManagement />}/>
                    <Route path="/paying-guest/add" element={<AddPGDetails />} />
                    <Route path="/paying-guest/view/:pgId" element={<PGDetailView />} />
                    <Route path="/paying-guest/list" element={<ViewPGList />} />
                    {/* Nested routes for Building Management */}
                    <Route path="/building" element={<BuildingManagement />} />
                    <Route path="/building/add" element={<AddBuilding />} />
                    <Route path="/building/list" element={<ViewBuildings />} />
                    <Route path="/building/vacate" element={<VacateBuilding />} />
                    <Route path="/building/invoice/create" element={<CreateRentInvoices />} />
                    <Route path="/building/invoice/status" element={<ViewRentalInvoiceStatus />} />
                    <Route path="/building/view/:buildingId" element={<ViewBuildingDetails />} />
                    {/* Nested routes for Bed Allocation Management */}
                    <Route path="/bedallocation" element={<BedAllocationManagement />} />
                    <Route path="/bedallocation/allocate-bed" element={<AllocateBed />} />
                    <Route path="/bedallocation/move-bed" element={<MoveBed />} />
                    <Route path="/bedallocation/view-allocated-beds" element={<ViewAllocatedBeds />} />
                    <Route path="/bedallocation/view-allocation-history" element={<ViewAllocationHistory />} />
                    <Route path="/bedallocation/view-soon-vacating-beds" element={<ViewSoonVacatingBeds />} />
                    <Route path="/bedallocation/view-vacant-beds" element={<ViewVacantBeds />} />
                    <Route path="/bedallocation/bed-pricing" element={<BedPricing />} />
                    <Route path="/bedallocation/allocation/:allocationId" element={<ViewAllocationDetails />} />
                    {/* Nested routes for Manage Users */}
                    <Route path="/staffs" element={<StaffManagement />} />
                    <Route path="/staffs/add" element={<AddStaff />} />
                    <Route path="/staffs/edit/:staffId" element={<AddStaff />} />
                    <Route path="/staffs/view" element={<ViewStaffs />} />
                    <Route path="/staffs/view/:staffId" element={<ViewStaffDetails />} />
                    <Route path="/staffs/types" element={<StaffTypesList onBack={() => window.history.back()} />} />
                    <Route path="/staffs/complaint-mapping" element={<StaffComplaintMapping onBack={() => window.history.back()} />} />
                    {/* Nested routes for Complaint Management */}
                    <Route path="/complaints" element={<ComplaintManagement />} />
                    <Route path="/complaints/add" element={<AddComplaint />} />
                    <Route path="/complaints/view" element={<ViewOpenComplaints />} />
                    <Route path="/complaints/view/:complaintId" element={<ViewComplaintDetails />} />
                    <Route path="/complaints/update/:complaintId" element={<AddComplaint />} />
                    <Route path="/complaints/history" element={<ViewComplaintsHistory />} />
                    <Route path="/complaints/my-assigned" element={<MyAssignedComplaints />} />
                    {/* Nested routes for Payment Management */}
                    <Route path="/payments" element={<PaymentManagement />} />
                    <Route path="/payments/add" element={<AddPayment />} />
                    <Route path="/payments/generate-monthly" element={<GenerateMonthlyRentalInvoice />} />
                    <Route path="/payments/view-draft-invoices" element={<ViewDraftInvoices />} />
                    <Route path="/payments/add-eb-bill" element={<AddEBBillToInvoices />} />
                    <Route path="/payments/view-unpaid-invoices" element={<ViewUnpaidInvoices />} />
                    <Route path="/payments/invoices/create" element={<CreateInvoice />} />
                    <Route path="/payments/invoices/:invoiceId/details" element={<ViewInvoiceDetails />} />
                    <Route path="/payments/:paymentId/view" element={<ViewPaymentDetail />} />
                    <Route path="/payments/payment-types" element={<PaymentTypesList />} />
                    <Route path="/payments/payment-methods" element={<PaymentMethodsList />} />
                    <Route path="/payments/view-payments" element={<ViewPayments />} />
                    <Route path="/payments/add-expense" element={<AddExpense />} />
                    <Route path="/payments/view-expenses" element={<ViewExpenses />} />
                    {/* Nested routes for Access Control */}
                    <Route path="/access-control" element={<AccessControl />} />
                    <Route path="/access-control/manage-users" element={<ManageUsers />} />
                    <Route path="/access-control/role-settings" element={<RoleSettings />} />
                    <Route path="/login-management" element={<AccessControl />} />
                    <Route path="/login-management/manage-users" element={<ManageUsers />} />
                    <Route path="/login-management/role-settings" element={<RoleSettings />} />
                    {/* Nested routes for Settings Management */}
                    <Route path="/settings" element={<SettingsManagement />} />
                    <Route path="/settings/change-password" element={<Navigate to="/profile/change-password" replace />} />
                    <Route path="/settings/payment-settings" element={<PaymentSettings />} />
                    <Route path="/settings/payment-types" element={<PaymentTypesList />} />
                    <Route path="/settings/payment-methods" element={<PaymentMethodsList />} />
                    <Route path="/settings/invoice-item-types" element={<InvoiceItemTypesList />} />
                    <Route path="/settings/whatsapp-settings" element={<WhatsAppSettings />} />
                    <Route path="/settings/whatsapp-templates" element={<WhatsAppTemplateManagement />} />
                    <Route path="/settings/whatsapp-template-tags" element={<WhatsAppTemplateTags />} />
                    <Route path="/settings/whatsapp-send-template" element={<SendWhatsAppTemplateMessage />} />
                    <Route path="/settings/expense-categories" element={<ExpenseCategoriesList />} />
                    {/* Profile (account) routes */}
                    <Route path="/profile/change-password" element={<ChangePasswordForm />} />
                    <Route path="/profile/change-username" element={<ChangeUsernameForm />} />
                    {/* Nested routes for Reports Management */}
                    <Route path="/reports" element={<ReportManagement />} />
                    <Route path="/reports/whatsapp-logs" element={<WhatsAppLogReport />} />
                    <Route path="/reports/allocations" element={<AllocationReport />} />
                    <Route path="/reports/payment-analytics" element={<PaymentAnalyticsReport />} />
                    <Route path="/reports/complaints" element={<ComplaintsReport />} />
                    <Route path="/reports/rent-collection" element={<RentCollectionReport />} />
                    <Route path="/reports/expenses" element={<ExpenseReport />} />
                    <Route path="/reports/jobs" element={<JobReportList />} />
                    <Route path="/reports/jobs/:jobId" element={<ViewJobDetails />} />
                    {/* Add more routes as needed */}
                </Routes>
            </div>
          </MenuCatalogProvider>
        </Router>
        </SessionProvider>
    );
}

export default App;